import { useMemo } from 'react'
import { type ColumnDef } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal, Pencil, Trash2, Power, Plus, Repeat } from 'lucide-react'
import {
  DataTable,
  ColumnVisibilityMenu,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
} from '@/components/ui/data-table'
import { useTablePreferences } from '@/hooks/useTablePreferences'
import {
  CATEGORIA_OBLIGACION_LABELS,
  PERIODICIDAD_OBLIGACION_LABELS,
  COLORES_OBLIGACION,
  type CategoriaObligacion,
  type PeriodicidadObligacion,
  type ColorObligacion,
} from './forms/obligacionRecurrenteSchema'

const COLOR_SWATCHES: Record<ColorObligacion, string> = Object.fromEntries(
  COLORES_OBLIGACION.map((c) => [c, `bg-${c}-500`])
) as Record<ColorObligacion, string>

// Safelist: bg-gray-500 bg-red-500 bg-orange-500 bg-amber-500 bg-yellow-500 bg-green-500 bg-emerald-500 bg-teal-500 bg-cyan-500 bg-blue-500 bg-sky-500 bg-indigo-500 bg-violet-500 bg-purple-500
import type { ObligacionRecurrenteConClientes } from '@/services/obligacionesRecurrentesService'

interface ObligacionesRecurrentesTableProps {
  obligaciones: ObligacionRecurrenteConClientes[]
  isLoading?: boolean
  onNueva: () => void
  onEditar: (obligacion: ObligacionRecurrenteConClientes) => void
  onEliminar: (obligacion: ObligacionRecurrenteConClientes) => void
  onToggle: (obligacion: ObligacionRecurrenteConClientes) => void
}

const CATEGORIA_VARIANTS: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  impositiva: 'default',
  laboral: 'secondary',
  contable: 'secondary',
  societaria: 'outline',
  previsional: 'secondary',
  otra: 'outline',
}

export function ObligacionesRecurrentesTable({
  obligaciones,
  isLoading,
  onNueva,
  onEditar,
  onEliminar,
  onToggle,
}: ObligacionesRecurrentesTableProps) {
  const {
    visibility,
    saveVisibility,
    columnSizing,
    saveColumnSizing,
    resetVisibility,
    resetColumnSizing,
    resetAll,
  } = useTablePreferences('obligaciones-recurrentes', {
    defaultVisibility: {
      nombre: true,
      categoria: true,
      periodicidad: true,
      dia_vencimiento: true,
      alcance: true,
    },
  })

  const columns = useMemo<ColumnDef<ObligacionRecurrenteConClientes>[]>(
    () => [
      {
        id: 'nombre',
        accessorKey: 'nombre',
        header: 'Nombre',
        meta: { label: 'Nombre' },
        size: 220,
        minSize: 150,
        maxSize: 350,
        cell: ({ row }) => {
          const isInactivo = !row.original.activo
          return (
            <div className="flex items-center gap-2">
              {row.original.color && (
                <div className={`h-2.5 w-2.5 rounded-full ${COLOR_SWATCHES[row.original.color as ColorObligacion] ?? 'bg-blue-500'} shrink-0`} />
              )}
              <span className={`font-medium ${isInactivo ? 'text-muted-foreground line-through' : ''}`}>
                {row.original.nombre}
              </span>
              {isInactivo && (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  Inactiva
                </Badge>
              )}
            </div>
          )
        },
      },
      {
        id: 'categoria',
        accessorFn: (row) => CATEGORIA_OBLIGACION_LABELS[row.categoria as CategoriaObligacion] ?? row.categoria,
        header: 'Categoría',
        meta: { label: 'Categoría' },
        size: 130,
        minSize: 100,
        maxSize: 180,
        cell: ({ row }) => {
          const cat = row.original.categoria as CategoriaObligacion
          return (
            <Badge variant={CATEGORIA_VARIANTS[cat] ?? 'outline'}>
              {CATEGORIA_OBLIGACION_LABELS[cat] ?? cat}
            </Badge>
          )
        },
      },
      {
        id: 'periodicidad',
        accessorFn: (row) => PERIODICIDAD_OBLIGACION_LABELS[row.periodicidad as PeriodicidadObligacion] ?? row.periodicidad,
        header: 'Periodicidad',
        meta: { label: 'Periodicidad' },
        size: 120,
        minSize: 90,
        maxSize: 160,
        cell: ({ row }) => {
          const p = row.original.periodicidad as PeriodicidadObligacion
          return (
            <span className="text-sm text-muted-foreground">
              {PERIODICIDAD_OBLIGACION_LABELS[p] ?? p}
            </span>
          )
        },
      },
      {
        id: 'dia_vencimiento',
        accessorKey: 'dia_vencimiento',
        header: 'Día Vto.',
        meta: { label: 'Día Vencimiento' },
        size: 100,
        minSize: 80,
        maxSize: 130,
        cell: ({ row }) => {
          const dia = row.original.dia_vencimiento
          const offset = row.original.mes_offset
          return (
            <span className="text-sm">
              Día {dia}
              {offset > 0 && (
                <span className="text-muted-foreground ml-1">(+{offset} mes{offset > 1 ? 'es' : ''})</span>
              )}
            </span>
          )
        },
      },
      {
        id: 'alcance',
        accessorFn: (row) => {
          const modo = row.modo_asignacion ?? (row.aplica_a_todos ? 'todos' : 'filtros')
          return modo === 'todos' ? 'Todos' : modo === 'clientes' ? 'Clientes' : 'Filtrado'
        },
        header: 'Alcance',
        meta: { label: 'Alcance' },
        size: 150,
        minSize: 100,
        maxSize: 200,
        cell: ({ row }) => {
          const modo = row.original.modo_asignacion ?? (row.original.aplica_a_todos ? 'todos' : 'filtros')
          if (modo === 'todos') {
            return <Badge variant="secondary">Todos</Badge>
          }
          if (modo === 'clientes') {
            const n = row.original.clientes_filtro?.length ?? 0
            return <Badge variant="outline">{n} cliente{n === 1 ? '' : 's'}</Badge>
          }
          const filtroIva = row.original.condicion_iva_filtro
          const filtroImp = row.original.impuesto_filtro
          const parts: string[] = []
          if (filtroIva?.length) parts.push(`${filtroIva.length} cond. IVA`)
          if (filtroImp?.length) parts.push(`${filtroImp.length} imp.`)
          return (
            <Badge variant="outline">
              {parts.length > 0 ? parts.join(' + ') : 'Sin filtros'}
            </Badge>
          )
        },
      },
      {
        id: 'acciones',
        header: '',
        enableHiding: false,
        enableSorting: false,
        enableResizing: false,
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          const obligacion = row.original
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEditar(obligacion)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onToggle(obligacion)}>
                    <Power className="mr-2 h-4 w-4" />
                    {obligacion.activo ? 'Desactivar' : 'Activar'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onEliminar(obligacion)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
      },
    ],
    [onEditar, onEliminar, onToggle]
  )

  const table = useReactTable({
    data: obligaciones,
    columns,
    state: {
      columnVisibility: visibility,
      columnSizing,
    },
    onColumnVisibilityChange: saveVisibility,
    onColumnSizingChange: saveColumnSizing,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [{ id: 'nombre', desc: false }],
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Button onClick={onNueva} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Obligación
        </Button>
        <ColumnVisibilityMenu
          table={table}
          onResetVisibility={resetVisibility}
          onResetColumnSizing={resetColumnSizing}
          onResetAll={resetAll}
        />
      </div>

      <DataTable
        table={table}
        isLoading={isLoading}
        emptyMessage="No hay obligaciones recurrentes configuradas"
        emptyIcon={<Repeat className="h-6 w-6 text-muted-foreground" />}
        enableColumnResizing
      />
    </div>
  )
}
